import {
  getPendingSyncEntries, removeSyncEntry, markSyncEntryFailed, markScoreSynced,
  markTeamScoreSynced, markHoleStatSynced, markHoleStatRejected, retryFailedSyncEntries,
  dueNowAllPending,
} from './offlineDb';
import { SLOW_TIMEOUT } from './useApi';

let processing = false;

/**
 * Answers a stat write will never talk its way out of, so the entry is dropped
 * rather than retried:
 *
 *   400  the payload is malformed, or the hole is not 1-18
 *   403  stats capture is not (or is no longer) part of this user's plan
 *   404  the round is gone, or belongs to another society
 *
 * Scoped to stat entries on purpose — this does not change how a queued SCORE
 * behaves. Without it these three climbed the backoff to a dead letter and were
 * then revived on every app start, re-attempting for ever against a round that
 * no longer exists.
 */
const PERMANENT_STAT_REJECTIONS = new Set([400, 403, 404]);

export async function processSyncQueue(authenticatedFetch) {
  if (processing) return;
  if (!navigator.onLine) return;

  processing = true;
  // Whether this run actually moved anything.
  //
  // The `finally` used to announce `syncQueueChanged` unconditionally — and the
  // listener that starts a drain is subscribed to exactly that event, so every
  // run scheduled another run, for ever, on an empty queue as much as a full
  // one. Async, so nothing froze; it just sat there reading IndexedDB in a loop
  // for as long as the app was open. Announcing a change only when there IS one
  // is what makes the drain terminate.
  let changed = false;
  try {
    const entries = await getPendingSyncEntries();

    for (const entry of entries) {
      try {
        const response = await authenticatedFetch(entry.endpoint, {
          method: entry.method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entry.payload),
          timeout: SLOW_TIMEOUT,
        });

        if (response.ok) {
          await removeSyncEntry(entry.id);
          // `statHole` is tested FIRST: a stats entry also carries a roundId
          // and a holeNumber, so without it a synced stat would mark a score
          // as synced instead — and leave the stat looking pending forever.
          if (entry.statHole && entry.roundId && entry.holeNumber) {
            await markHoleStatSynced(entry.roundId, entry.holeNumber);
          } else if (entry.roundId && entry.holeNumber) {
            await markScoreSynced(entry.roundId, entry.holeNumber);
          } else if (entry.teamRoundId && entry.holeNumber) {
            await markTeamScoreSynced(entry.teamRoundId, entry.holeNumber);
          }
          changed = true;
        } else if (response.status === 401) {
          // Token expired — Firebase will auto-refresh, retry next cycle
          break;
        } else if (entry.statHole && PERMANENT_STAT_REJECTIONS.has(response.status)) {
          // Nothing about retrying changes any of these, and a stat is not
          // worth keeping a dead row in the queue for — drop it.
          await removeSyncEntry(entry.id);
          // And say so on the row itself. A local row with no queue entry is
          // exactly what `reconcileOrphanedStats` exists to revive, so dropping
          // the entry without this would put it straight back on the queue at
          // the next foreground, for ever.
          if (entry.roundId && entry.holeNumber) {
            await markHoleStatRejected(entry.roundId, entry.holeNumber);
          }
          changed = true;
        } else {
          // Backoff, not burial — see `markSyncEntryFailed`.
          await markSyncEntryFailed(entry.id);
          changed = true;
        }
      } catch (err) {
        // Network error or timeout. Stop the run — the rest of the queue will
        // fail the same way — but COUNT it against this entry, so the drain
        // below has a due time to wake up on rather than waiting for the
        // player to happen to tap something else.
        await markSyncEntryFailed(entry.id);
        changed = true;
        break;
      }
    }
  } finally {
    processing = false;
    if (changed) window.dispatchEvent(new CustomEvent('syncQueueChanged'));
  }
}

/** Everything the player asked for, now: dead letters revived, backoffs
 *  cleared, one drain. Behind the "Retry" on the stats screen. */
export async function forceSyncNow(authenticatedFetch) {
  await retryFailedSyncEntries();
  await dueNowAllPending();
  await processSyncQueue(authenticatedFetch);
}

let cleanupFn = null;
let refCount = 0;

/**
 * Drive the offline queue for as long as anything holds a reference.
 *
 * REFERENCE COUNTED, and mounted app-wide (see `SyncProcessor` in App.js).
 * Both used to be untrue and each was a way to lose a write:
 *
 *  - It was started only by the two live-scoring screens, so the standalone
 *    round-stats screen — a finalised round being filled in afterwards, which
 *    is the COMMON case for stats — queued writes that nothing was listening
 *    for. They sat in IndexedDB until the player opened a scoring screen for
 *    that round, which for a finalised round never happens.
 *  - The `cleanupFn` guard meant a second caller got the FIRST caller's
 *    cleanup, so whichever screen unmounted first tore down the listeners the
 *    other was relying on.
 */
export function startSyncProcessor(authenticatedFetch) {
  refCount += 1;
  if (cleanupFn) return makeRelease();

  let timer = null;

  const drain = () => {
    if (navigator.onLine) processSyncQueue(authenticatedFetch);
  };

  // Anything that failed while the app was last open is worth one more attempt
  // now — a new session usually means a different network. `reset: false` keeps
  // the retry count, so an entry failing for a reason a new network cannot fix
  // gets one attempt per session rather than a fresh twelve every time.
  retryFailedSyncEntries({ reset: false }).then(drain, drain);

  const onOnline = drain;
  window.addEventListener('online', onOnline);

  // Process when a new entry is added (or one is confirmed) while online.
  const onQueueChanged = drain;
  window.addEventListener('syncQueueChanged', onQueueChanged);

  // Coming back to the app is the single best moment to try again: it is when
  // the phone has most likely just found signal. `navigator.onLine` cannot tell
  // us that — it reports an interface, not reachability, so on a train it stays
  // true through every tunnel and the 'online' event never fires at all.
  const onVisible = () => { if (document.visibilityState === 'visible') drain(); };
  document.addEventListener('visibilitychange', onVisible);

  // The backstop. Without it a queue that stalls on a network error stays
  // stalled until the player happens to write something else — so the LAST
  // holes logged, the ones nothing follows, were the ones most likely to be
  // lost.
  //
  // It drains UNCONDITIONALLY, and deliberately: `processSyncQueue` already
  // reads only what is due and does nothing when that is nothing, so the tick
  // costs one indexed read. Asking first was worse than free — it asked
  // `getNextAttemptAt`, which reports the earliest attempt still in the FUTURE
  // and therefore says "wait" whenever anything at all is backing off, even
  // with other entries due right now. One write that had climbed to the
  // one-hour backoff silenced the backstop for every write logged after it,
  // which is precisely the case it exists for.
  timer = setInterval(drain, 30000);

  cleanupFn = () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('syncQueueChanged', onQueueChanged);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
    cleanupFn = null;
  };

  return makeRelease();
}

function makeRelease() {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refCount -= 1;
    // Only the last holder tears it down.
    if (refCount <= 0) {
      refCount = 0;
      cleanupFn?.();
    }
  };
}
